import { useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import CreditCardOutlined from "@mui/icons-material/CreditCardOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import TrendingDownRounded from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import ChartCard from "../components/ChartCard";
import DreSimulationHistoryPanel from "../components/DreSimulationHistoryPanel";
import EmpreendimentosIntersecaoNotice from "../components/EmpreendimentosIntersecaoNotice";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import {
  getContabilidadeApiErrorMessage,
  listarDreViabilidadeVersoes,
  logContabilidadeApiError,
  obterDespesas,
  obterDreViabilidadeVersao,
} from "../services/agenteContabilidadeApiService";
import { buildEmpresaFilterParams, buildEmpreendimentoOptions, isTodosEmpreendimentos } from "../utils/empreendimentoSelector";
import {
  consolidateTimelineByMonth,
  getIntersecaoForMonth,
  normalizeIntersecaoList,
  normalizeIntersecaoPorMes,
  resolveActiveMonth,
} from "../utils/intersecaoEmpreendimentos";
import {
  listSelectedDreSimulations,
  readSelectedDreReference,
  resolveInternalEmpresaId,
  writeSelectedDreReference,
  writeSelectedDreSimulation,
} from "../utils/dreSimulationSession";
import { sortByMonthTimeline } from "../utils/monthTimeline";
import { colors } from "../constants/designTokens";
import { formatCurrency } from "../utils/formatters";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function normalizeDespesasItem(item) {
  return {
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    idClienteEmpresa: getFirstValue(item, ["idClienteEmpresa", "id_cliente_empresa"], ""),
    nomeEmpresa: getFirstValue(item, ["nomeEmpresa", "nome_empresa"], ""),
    vgvContratado: toNumber(getFirstValue(item, ["vgvContratado", "vgv_contratado", "vgv"])),
    receitaApropriada: toNumber(getFirstValue(item, ["receitaApropriada", "receita_apropriada"])),
    corretagem: toNumber(getFirstValue(item, ["corretagem", "comissoes", "comissoes_corretagem"])),
  };
}

function normalizeDespesasResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeDespesasItem).filter((item) => item.mes_ano);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);

  return {
    months,
    timeline,
    empreendimentosIntersecao: normalizeIntersecaoList(
      payload.empreendimentosIntersecao ||
        payload.empreendimentos_intersecao ||
        payload.intersecaoEmpreendimentos ||
        payload.intersecao_empreendimentos
    ),
    empreendimentosIntersecaoPorMes: normalizeIntersecaoPorMes(payload),
  };
}

function buildDreDespesasSnapshot(simulation, activeMonth, systemSnapshot = null) {
  if (!simulation?.id || !activeMonth) {
    return null;
  }

  const apropriado = simulation.colunas?.apropriado || {};
  const getApropriado = (lineKey) => toNumber(apropriado[lineKey]);
  const corretagem = toNumber(systemSnapshot?.corretagem);
  const publicidade = getApropriado("publicidade");
  const admCentral = getApropriado("despesas_adm_central");
  const despTributarias = getApropriado("despesa_tributaria");
  const receitasFinanceiras = getApropriado("receita_financeira");
  const despesasFinanceiras = getApropriado("custos_estruturacao_financeira");

  return {
    mes_ano: activeMonth,
    nomeVersao: simulation.nomeVersao || "",
    corretagem,
    publicidade,
    admCentral,
    despTributarias,
    totalDespesasOperacionais: corretagem + publicidade + admCentral + despTributarias,
    receitasFinanceiras,
    despesasFinanceiras,
    resultadoFinanceiro: receitasFinanceiras - despesasFinanceiras,
  };
}

function buildDreDespesasTimeline(simulations = [], selectedReference, systemTimelineByMonth = new Map()) {
  if (!selectedReference?.id) {
    return [];
  }

  const sourceSimulations = simulations.length ? simulations : [selectedReference];

  return sortByMonthTimeline(sourceSimulations
    .filter((simulation) => String(simulation.id) === String(selectedReference.id))
    .map((simulation) => {
      const mesAno = simulation.mes_ano || simulation.mesAnoBase;
      return buildDreDespesasSnapshot(simulation, mesAno, systemTimelineByMonth.get(mesAno));
    })
    .filter(Boolean));
}

function buildDreDespesasSimulation(version) {
  if (!version?.id) {
    return null;
  }

  return {
    id: version.id,
    nomeVersao: version.nomeVersao || "",
    descricao: version.descricao || "",
    mesAnoBase: version.mesAnoBase || "",
    linhas: version.linhas || {},
    colunas: version.colunas || {},
  };
}

export default function DespesasPage() {
  const navigate = useNavigate();
  const { tenant, state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("despesas");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiDespesasData, setApiDespesasData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");
  const [versions, setVersions] = useState([]);
  const [versionsStatus, setVersionsStatus] = useState("idle");
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [selectedDreSimulation, setSelectedDreSimulation] = useState(null);
  const [versionMessage, setVersionMessage] = useState(null);

  const backendEmpreendimentos = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpreendimentos = backendEmpreendimentos.length > 0;
  const backendEmpresaOptions = useMemo(
    () => buildEmpreendimentoOptions(backendEmpreendimentos),
    [backendEmpreendimentos]
  );
  const [selectedBackendEmpresa, setSelectedBackendEmpresa] = usePageEmpresaSelection("despesas", backendEmpresaOptions);
  const selectedBackendEmpresaId = useMemo(
    () => resolveInternalEmpresaId(backendEmpreendimentos, selectedBackendEmpresa),
    [backendEmpreendimentos, selectedBackendEmpresa]
  );

  useEffect(() => {
    const storedReference = readSelectedDreReference(tenant.clientId, selectedBackendEmpresaId);
    setSelectedDreSimulation(storedReference);
    setSelectedVersionId(storedReference?.id ? String(storedReference.id) : "");
    setVersionMessage(null);
  }, [selectedBackendEmpresaId, tenant.clientId]);

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchDespesas() {
      if (!backendCatalogSyncKey && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      if (hasBackendEmpreendimentos && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      setApiStatus("loading");

      try {
        const params = buildEmpresaFilterParams(selectedBackendEmpresa);
        const payload = await obterDespesas(params);
        const normalizedData = normalizeDespesasResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiDespesasData(normalizedData);
          setApiStatus("success");
        } else {
          setApiDespesasData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("despesas", error);
        setApiStatus("error");
      }
    }

    fetchDespesas();

    return () => {
      active = false;
    };
  }, [backendCatalogSyncKey, hasBackendEmpreendimentos, selectedBackendEmpresa]);

  useEffect(() => {
    let active = true;

    async function fetchVersions() {
      if (!selectedBackendEmpresaId) {
        setVersions([]);
        setVersionsStatus("idle");
        return;
      }

      setVersionsStatus("loading");
      try {
        const payload = await listarDreViabilidadeVersoes({
          idClienteEmpresa: selectedBackendEmpresaId,
        });
        if (!active) return;
        setVersions(Array.isArray(payload) ? payload : []);
        setVersionsStatus("success");
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("despesas-dre-viabilidade-versoes", error);
        setVersions([]);
        setVersionsStatus("error");
      }
    }

    fetchVersions();

    return () => {
      active = false;
    };
  }, [selectedBackendEmpresaId]);

  const usingApiData = Boolean(apiDespesasData?.timeline?.length);
  const despesasData = useMemo(() => {
    if (usingApiData) {
      return {
        ...apiDespesasData,
        months: apiDespesasData.months,
        timeline: apiDespesasData.timeline,
      };
    }

    return { months: [], timeline: [] };
  }, [apiDespesasData, usingApiData]);

  const { timeline } = despesasData;
  const isTodos = isTodosEmpreendimentos(selectedBackendEmpresa);
  const visibleTimeline = useMemo(
    () => (isTodos ? consolidateTimelineByMonth(timeline, ["aliquotaTotalImpostos"]) : timeline),
    [isTodos, timeline]
  );
  const systemTimelineByMonth = useMemo(
    () => new Map(visibleTimeline.map((item) => [item.mes_ano, item])),
    [visibleTimeline]
  );
  const dreDespesasTimeline = useMemo(
    () => buildDreDespesasTimeline(
      selectedDreSimulation
        ? [
            ...listSelectedDreSimulations(tenant.clientId, selectedBackendEmpresaId),
            selectedDreSimulation,
          ]
        : listSelectedDreSimulations(tenant.clientId, selectedBackendEmpresaId),
      selectedDreSimulation,
      systemTimelineByMonth
    ),
    [selectedBackendEmpresaId, selectedDreSimulation, systemTimelineByMonth, tenant.clientId]
  );
  const dreDespesasChartData = useMemo(
    () => dreDespesasTimeline.map((item) => ({
      ...item,
      corretagem: item.corretagem / 1000000,
      publicidade: item.publicidade / 1000000,
      admCentral: item.admCentral / 1000000,
      despTributarias: item.despTributarias / 1000000,
    })),
    [dreDespesasTimeline]
  );
  const shouldShowDreDespesasChart = dreDespesasTimeline.length >= 3;
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : despesasData.months;

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const intersecaoEmpreendimentos = getIntersecaoForMonth(despesasData, activeMonth);
  const dreDespesasSnapshot = useMemo(
    () => dreDespesasTimeline.find((item) => item.mes_ano === activeMonth) || null,
    [activeMonth, dreDespesasTimeline]
  );
  const hasDreDespesasSimulation = Boolean(dreDespesasSnapshot);
  const hasVersionHistory = versions.length > 0;
  const canShowSimulationFlow = Boolean(
    selectedBackendEmpresaId &&
      (hasVersionHistory || selectedDreSimulation || versionsStatus === "loading")
  );

  const empreendimentoOptions = backendEmpresaOptions;
  const selectedEmpreendimentoValue = selectedBackendEmpresa;

  function handleEmpreendimentoChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedBackendEmpresa(value || "");
    setApiAvailableMonths([]);
    setVersions([]);
    setVersionsStatus("idle");
    setSelectedVersionId("");
    setSelectedDreSimulation(null);
    setVersionMessage(null);
  }

  async function handleVersionChange(value) {
    setSelectedVersionId(value || "");
    setVersionMessage(null);

    if (!value) {
      return;
    }

    try {
      const version = await obterDreViabilidadeVersao(value);
      const selectedSimulation = buildDreDespesasSimulation(version);
      if (!selectedSimulation) {
        setVersionMessage({ severity: "warning", text: "Nao foi possivel carregar a simulacao selecionada." });
        return;
      }

      const simulationMonth = selectedSimulation.mesAnoBase || activeMonth;
      setSelectedDreSimulation(selectedSimulation);
      if (simulationMonth) {
        setSelectedMonth(simulationMonth);
        writeSelectedDreSimulation(
          tenant.clientId,
          selectedBackendEmpresaId,
          simulationMonth,
          selectedSimulation
        );
      }
      writeSelectedDreReference(
        tenant.clientId,
        selectedBackendEmpresaId,
        selectedSimulation
      );
    } catch (error) {
      logContabilidadeApiError("despesas-obter-versao-dre", error);
      setVersionMessage({
        severity: "error",
        text: getContabilidadeApiErrorMessage(
          error,
          "Nao foi possivel carregar a simulacao selecionada."
        ),
      });
    }
  }

  const metrics = dreDespesasSnapshot
    ? [
        { label: "Corretagem", value: formatCurrency(dreDespesasSnapshot.corretagem), variant: "purple", icon: BusinessRounded },
        { label: "Publicidade", value: formatCurrency(dreDespesasSnapshot.publicidade), variant: "blue", icon: TrendingUpRounded },
        { label: "Adm. Central", value: formatCurrency(dreDespesasSnapshot.admCentral), variant: "orange", icon: BusinessRounded },
        { label: "Desp. Tributarias", value: formatCurrency(dreDespesasSnapshot.despTributarias), variant: "red", icon: ReceiptLongOutlined },
        { label: "Total Desp. Operacionais", value: formatCurrency(dreDespesasSnapshot.totalDespesasOperacionais), variant: "navy", icon: DescriptionOutlined },
        { label: "Receitas Financeiras", value: formatCurrency(dreDespesasSnapshot.receitasFinanceiras), variant: "green", icon: AttachMoneyRounded },
        { label: "Despesas Financeiras", value: formatCurrency(dreDespesasSnapshot.despesasFinanceiras), variant: "red", icon: TrendingDownRounded },
        { label: "Resultado Financeiro", value: formatCurrency(dreDespesasSnapshot.resultadoFinanceiro), variant: "magenta", icon: CreditCardOutlined },
      ]
    : [];

  const isLoadingDespesas = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;

  return (
    <AgenteContabilidadeShell
      title="Gestão de Despesas"
      subtitle="Despesas operacionais e resultado financeiro"
      updating={apiStatus === "loading" && usingApiData}
      agenteContext={{
        paginaAtual: "despesas",
        empresa: selectedBackendEmpresa,
        competencia: activeMonth,
        dadosVisiveis: dreDespesasSnapshot || {
          mensagem: "Despesas dependem de uma simulacao selecionada na DRE de Viabilidade para a mesma empresa e competencia.",
        },
      }}
      actions={[
        <FilterSelect
          key="emp"
          value={selectedEmpreendimentoValue}
          onChange={handleEmpreendimentoChange}
          options={empreendimentoOptions}
          icon={BusinessRounded}
          minWidth={286}
        />,
      ]}
    >
      {isLoadingDespesas ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <CircularProgress size={34} />
            <Typography sx={{ fontWeight: 900, color: colors.text }}>
              Carregando dados de despesas
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Aguarde enquanto consultamos as bases calculadas pela API.
            </Typography>
          </Stack>
        </Paper>
      ) : !visibleTimeline.length && !canShowSimulationFlow ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.2} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
              Nenhum dado disponivel
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 14, maxWidth: 520 }}>
              Nao encontramos dados disponiveis para exibicao no momento.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <>
          <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
          <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
          <EmpreendimentosIntersecaoNotice
            isTodosEmpreendimentos={isTodos}
            activeMonth={activeMonth}
            empreendimentos={intersecaoEmpreendimentos}
          />
          {hasDreDespesasSimulation ? (
            <Stack spacing={2.4}>
              <KpiGrid metrics={metrics} />
              {shouldShowDreDespesasChart ? (
                <ChartCard
                  title="Timeline da Simulação"
                  subtitle="Competências preenchidas na DRE de Viabilidade"
                  data={dreDespesasChartData}
                  height={320}
                  headerGradient="purple"
                  viewTabs={[
                    {
                      key: "operacionais",
                      label: "Operacionais",
                      series: [
                        { key: "corretagem", name: "Corretagem", color: "#7c3aed" },
                        { key: "publicidade", name: "Publicidade", color: "#2563eb" },
                        { key: "admCentral", name: "Adm. Central", color: "#f97316" },
                        { key: "despTributarias", name: "Desp. Tributarias", color: "#ef4444" },
                      ],
                    },
                  ]}
                  defaultView="operacionais"
                />
              ) : null}
              {versionMessage ? (
                <Alert severity={versionMessage.severity}>
                  {versionMessage.text}
                </Alert>
              ) : null}
              <DreSimulationHistoryPanel
                versions={versions}
                versionsStatus={versionsStatus}
                selectedVersionId={selectedVersionId}
                onSelectVersion={handleVersionChange}
                onCreateVersion={() => navigate("/agentecontabilidade/dre-viabilidade")}
                subtitle="Selecione outra simulacao para atualizar os indicadores desta aba."
              />
            </Stack>
          ) : (
            <Stack spacing={2.4} sx={{ mt: 3 }}>
              <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
              <Stack spacing={1.2} sx={{ textAlign: "center", maxWidth: 760, mx: "auto" }}>
                <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
                  Nenhuma simulação selecionada para esta competência
                </Typography>
                <Typography sx={{ color: colors.muted, fontSize: 14 }}>
                  Para visualizar as despesas deste mês, selecione ou crie uma simulação na DRE de Viabilidade para esta empresa e competência.
                </Typography>
                {!versions.length && versionsStatus === "success" ? (
                  <Button
                    variant="contained"
                    onClick={() => navigate("/agentecontabilidade/dre-viabilidade")}
                    sx={{ alignSelf: "center", mt: 1 }}
                  >
                    Criar simulacao na DRE de Viabilidade
                  </Button>
                ) : null}
                {versionsStatus === "loading" ? (
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                      Carregando historico de simulacoes
                    </Typography>
                  </Stack>
                ) : null}
                {versionMessage ? (
                  <Alert severity={versionMessage.severity} sx={{ textAlign: "left" }}>
                    {versionMessage.text}
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
                <DreSimulationHistoryPanel
                  versions={versions}
                  versionsStatus={versionsStatus}
                  selectedVersionId={selectedVersionId}
                  onSelectVersion={handleVersionChange}
                  onCreateVersion={() => navigate("/agentecontabilidade/dre-viabilidade")}
                  subtitle="Selecione uma simulacao para carregar os indicadores de despesas."
                />
            </Stack>
          )}
        </>
      )}
    </AgenteContabilidadeShell>
  );
}
